'use client';

import { c as _c } from "react/compiler-runtime";
import './content-feed-card.scss';
import type { TFunction } from 'i18next';
import React, { useState } from 'react';
import { ButtonStyle } from '@sola/ark-core/atoms/button-style/button-style.tsx';
import { IntlBlock } from '@sola/ark-core/atoms/intl/Intl.tsx';
import { Link } from '@sola/ark-core/atoms/link/link.tsx';
import { Tooltip, TooltipContent, TooltipTrigger } from '@sola/ark-core/molecules/tooltip/tooltip.tsx';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.tsx';
import { getFeatureFlags } from '@sola/config/lib/getters.ts';
import type { ContentFeedAuthor, ContentFeedCommentResolved, ContentFeedFundingProgress, ContentFeedMedia, ContentFeedParticipant, ContentFeedQueryMatch, ContentFeedReactionSummary } from '@sola/schema/dotcom/proxy-api/content-feed.ts';
import type { IconNames } from '@sola/schema/lib/icons/icon-names.ts';
import { trackSelectContent } from '@sola/tracking/components/datalayer.tsx';
import { ContentFeedAvatar } from '../../atoms/content-feed-avatar/content-feed-avatar.tsx';
import { ContentFeedProgressBar } from '../../atoms/content-feed-progress-bar/content-feed-progress-bar.tsx';
import { ContentFeedComments } from '../../molecules/content-feed-comments/content-feed-comments.tsx';
import { ContentFeedGallery } from '../../molecules/content-feed-gallery/content-feed-gallery.tsx';
import { ContentFeedReactions } from '../../molecules/content-feed-reactions/content-feed-reactions.tsx';
import { useComponentContentFeedCard } from './content-feed-card.logic.ts';
export type { ContentFeedAuthor, ContentFeedFundingProgress, ContentFeedMedia } from '@sola/schema/dotcom/proxy-api/content-feed.ts';
export type ContentFeedMatch = ContentFeedQueryMatch;
export interface ContentFeedCardProps {
  /** Unique entry ID from the API (used for tracking) */
  entryId?: string;
  /** Map of participant localId → preferred name (for banner text resolution) */
  participantNameMap?: Map<string, string>;
  /** Participants directly associated with this entry (from the API response) */
  entryParticipants?: ContentFeedParticipant[];
  /** Content source type — determines which layout variant to render */
  sourceType?: 'causes' | 'community' | 'compassion.media' | 'cause-updates';
  /** Icon displayed in the top banner */
  icon?: IconNames;
  /** Entry title (resolved for locale) */
  title: string;
  /** Entry description (resolved for locale) — used by community/compassion.media sources */
  description?: string;
  /** Media assets from the entry */
  media?: ContentFeedMedia[];
  /** Funding progress metadata (Causes source only) */
  fundingProgress?: ContentFeedFundingProgress;
  /** Link metadata for the CTA */
  link?: {
    url: string;
  };
  /** Program country ISO code (e.g. "DO", "UG") */
  programCountryIsoCode?: string;
  /** Resolved country display name (looked up from isoCode) */
  programCountryName?: string;
  /** Query match context — drives the banner relationship message */
  match?: ContentFeedMatch;
  /** Custom banner text override. If not provided, derived from match data. */
  bannerText?: string;
  /** Whether to show the "New Update" badge on the first image */
  hasNewUpdate?: boolean;
  /** CTA button label override */
  ctaLabel?: string;
  /** ISO 8601 timestamp of when the entry was published */
  publishedAt?: string;
  /** Author of the entry content (community/compassion.media sources) */
  author?: ContentFeedAuthor;
  /** Reaction summaries from the API (emoji counts + user's reaction) */
  reactions?: ContentFeedReactionSummary[];
  /** Whether reactions are enabled for this entry */
  canReact?: boolean;
  /** Whether commenting is enabled for this entry */
  canComment?: boolean;
  /** Most recent comment on this entry */
  topComment?: ContentFeedCommentResolved | null;
  /** Total number of comments on this entry */
  commentCount?: number;
}
interface BannerNamesTooltipProps {
  /** Resolved child names; joined by i18next's `list` formatter in the locale. */
  names: string[];
  /** Translation function (scoped to the `my` namespace by the card). */
  t: TFunction;
  /** The emphasized banner segment supplied by IntlBlock (the bold count phrase). */
  children?: React.ReactNode;
}

/**
 * Renders the emphasized "N of your sponsored children" banner segment as a
 * tooltip trigger that reveals the full list of resolved names. Used as the
 * `strong` tag override for IntlBlock so only that phrase is interactive.
 *
 * The name list is joined in the locale via i18next's `list` formatter
 * (`{{names, list}}`), so conjunctions and separators follow the reader's
 * language rather than being pre-joined in TypeScript.
 */
const BannerNamesTooltip: React.FC<BannerNamesTooltipProps> = t0 => {
  const $ = _c(10);
  const {
    names,
    t,
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <TooltipTrigger><button type="button" className="ContentFeedCard__banner-names">{children}</button></TooltipTrigger>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== names || $[3] !== t) {
    t2 = t("my:contentFeed.bannerNamesTooltip", {
      names
    });
    $[2] = names;
    $[3] = t;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t2) {
    t3 = <TooltipContent size="sm" usePortal={true}><p>{t2}</p></TooltipContent>;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t1 || $[8] !== t3) {
    t4 = <strong><Tooltip>{t1}{t3}</Tooltip></strong>;
    $[7] = t1;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
};
export const ContentFeedCard: React.FC<ContentFeedCardProps> = props => {
  const $ = _c(63);
  const {
    entryId
  } = props;
  const [commentToggleSignal, setCommentToggleSignal] = useState(0);
  const {
    isCausesLayout,
    isPostLayout,
    author,
    authorLine,
    formattedPublishedAt,
    description,
    icon,
    title,
    fundingProgress,
    link,
    programCountryName,
    hasNewUpdate,
    galleryItems,
    allGalleryItems,
    totalPhotos,
    resolvedBannerText,
    bannerI18n,
    bannerTooltipNames,
    progressPercent,
    formattedRaised,
    formattedGoal,
    fundedMessage,
    isFundedOrClosed,
    lightboxItems,
    resolvedCtaLabel,
    t
  } = useComponentContentFeedCard(props);
  let t0;
  if ($[0] !== entryId || $[1] !== props.sourceType || $[2] !== title) {
    t0 = () => {
      if (entryId) {
        const type = props.sourceType ? `${props.sourceType}:cta` : "content-feed:cta";
        trackSelectContent(entryId, title, type, [type]);
      }
    };
    $[0] = entryId;
    $[1] = props.sourceType;
    $[2] = title;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const handleCtaClick = t0;
  let t1;
  if ($[4] !== bannerI18n || $[5] !== bannerTooltipNames || $[6] !== icon || $[7] !== resolvedBannerText || $[8] !== t) {
    t1 = (resolvedBannerText || bannerI18n) && <div className="ContentFeedCard__banner flex items-center gap-snug-xl py-comfy-xs px-comfy-sm sm:py-comfy-sm sm:px-comfy-lg"><ArkIcon iconName={icon} className="ContentFeedCard__banner-icon shrink-0 label-neutral-mild" size="1.5em" /><span className="label-moderate label-md label-neutral-mild">{resolvedBannerText || <IntlBlock i18nKey={bannerI18n.i18nKey} values={bannerI18n.values} components={bannerTooltipNames ? {
          strong: <BannerNamesTooltip names={bannerTooltipNames} t={t} />
        } : undefined} />}</span></div>;
    $[4] = bannerI18n;
    $[5] = bannerTooltipNames;
    $[6] = icon;
    $[7] = resolvedBannerText;
    $[8] = t;
    $[9] = t1;
  } else {
    t1 = $[9];
  }
  let t2;
  if ($[10] !== author || $[11] !== authorLine || $[12] !== formattedPublishedAt || $[13] !== isPostLayout || $[14] !== programCountryName) {
    t2 = isPostLayout && author && <div className="ContentFeedCard__post-header flex items-start justify-between p-comfy-xs md:pt-comfy-md md:pb-comfy-xs md:px-comfy-lg gap-comfy-sm"><div className="flex items-start gap-comfy-sm"><ContentFeedAvatar src={author.photo?.url} alt={author.displayName} /><div className="flex flex-col"><span className="label-potent label-md md:label-potent md:label-lg label-neutral-moderate mb-snug-sm">{authorLine}</span>{programCountryName && <div className="flex items-center gap-snug-sm label-neutral-mild"><ArkIcon iconName="marker-pin-01" size="1em" /><span className="label-md">{programCountryName}</span></div>}</div></div>{formattedPublishedAt && <span className="label-md label-neutral-mild shrink-0">{formattedPublishedAt}</span>}</div>;
    $[10] = author;
    $[11] = authorLine;
    $[12] = formattedPublishedAt;
    $[13] = isPostLayout;
    $[14] = programCountryName;
    $[15] = t2;
  } else {
    t2 = $[15];
  }
  let t3;
  if ($[16] !== allGalleryItems || $[17] !== entryId || $[18] !== galleryItems || $[19] !== hasNewUpdate || $[20] !== lightboxItems || $[21] !== props.sourceType || $[22] !== t || $[23] !== title || $[24] !== totalPhotos) {
    t3 = <ContentFeedGallery galleryItems={galleryItems} allGalleryItems={allGalleryItems} lightboxItems={lightboxItems} totalPhotos={totalPhotos} hasNewUpdate={hasNewUpdate} title={title} entryId={entryId} sourceType={props.sourceType} t={t} />;
    $[16] = allGalleryItems;
    $[17] = entryId;
    $[18] = galleryItems;
    $[19] = hasNewUpdate;
    $[20] = lightboxItems;
    $[21] = props.sourceType;
    $[22] = t;
    $[23] = title;
    $[24] = totalPhotos;
    $[25] = t3;
  } else {
    t3 = $[25];
  }
  let t4;
  if ($[26] !== description || $[27] !== formattedGoal || $[28] !== formattedRaised || $[29] !== fundedMessage || $[30] !== fundingProgress || $[31] !== handleCtaClick || $[32] !== isCausesLayout || $[33] !== isFundedOrClosed || $[34] !== link || $[35] !== programCountryName || $[36] !== progressPercent || $[37] !== resolvedCtaLabel || $[38] !== t || $[39] !== title) {
    t4 = isCausesLayout ? <div className="flex flex-col gap-comfy-sm p-comfy-lg pt-0">{programCountryName && <div className="flex items-center gap-snug-sm label-neutral-mild"><ArkIcon iconName="marker-pin-01" size="1.25em" /><span className="label-moderate label-md">{programCountryName}</span></div>}<h3 className="label-lg md:label-potent md:label-xl label-potent label-neutral-amplified">{title}</h3>{fundedMessage ? <p className="label-md label-neutral-mild">{fundedMessage}</p> : fundingProgress && <div className="flex flex-col gap-snug-lg"><ContentFeedProgressBar percent={progressPercent} ariaLabel={t("my:contentFeed.progressLabelA11y", {
          raised: formattedRaised,
          goal: formattedGoal
        })} /><p className="label-moderate label-md md:label-moderate md:label-lg label-neutral-mild"><IntlBlock i18nKey="my:contentFeed.progressLabel" values={{
            raised: formattedRaised,
            goal: formattedGoal
          }} /></p></div>}{link?.url && !isFundedOrClosed && <div className="pt-comfy-sm"><ButtonStyle variant="primary" size="lg" fullWidth={true}><Link href={link.url} onClick={handleCtaClick}>{resolvedCtaLabel}</Link></ButtonStyle></div>}</div> : description && <div className="flex flex-col gap-comfy-sm p-comfy-lg pt-0"><p className="label-md md:label-lg label-neutral-moderate ContentFeedCard__description">{description}</p></div>;
    $[26] = description;
    $[27] = formattedGoal;
    $[28] = formattedRaised;
    $[29] = fundedMessage;
    $[30] = fundingProgress;
    $[31] = handleCtaClick;
    $[32] = isCausesLayout;
    $[33] = isFundedOrClosed;
    $[34] = link;
    $[35] = programCountryName;
    $[36] = progressPercent;
    $[37] = resolvedCtaLabel;
    $[38] = t;
    $[39] = title;
    $[40] = t4;
  } else {
    t4 = $[40];
  }
  let t5;
  if ($[41] !== entryId || $[42] !== props.canComment || $[43] !== props.canReact || $[44] !== props.link?.url || $[45] !== props.reactions || $[46] !== title) {
    t5 = getFeatureFlags().enableMyContentFeedReactions && entryId && <ContentFeedReactions entryId={entryId} contentTitle={title} contentUrl={props.link?.url} reactions={props.reactions} canReact={props.canReact} onCommentClick={props.canComment ? () => setCommentToggleSignal(_temp) : undefined} />;
    $[41] = entryId;
    $[42] = props.canComment;
    $[43] = props.canReact;
    $[44] = props.link?.url;
    $[45] = props.reactions;
    $[46] = title;
    $[47] = t5;
  } else {
    t5 = $[47];
  }
  let t6;
  if ($[48] !== commentToggleSignal || $[49] !== entryId || $[50] !== props.canComment || $[51] !== props.commentCount || $[52] !== props.link?.url || $[53] !== props.topComment || $[54] !== title) {
    t6 = getFeatureFlags().enableMyContentFeedReactions && entryId && <ContentFeedComments entryId={entryId} contentTitle={title} contentUrl={props.link?.url} topComment={props.topComment} commentCount={props.commentCount} canComment={props.canComment} toggleSignal={commentToggleSignal} />;
    $[48] = commentToggleSignal;
    $[49] = entryId;
    $[50] = props.canComment;
    $[51] = props.commentCount;
    $[52] = props.link?.url;
    $[53] = props.topComment;
    $[54] = title;
    $[55] = t6;
  } else {
    t6 = $[55];
  }
  let t7;
  if ($[56] !== t1 || $[57] !== t2 || $[58] !== t3 || $[59] !== t4 || $[60] !== t5 || $[61] !== t6) {
    t7 = <article className="ContentFeedCard rounded-md overflow-hidden">{t1}{t2}{t3}{t4}{t5}{t6}</article>;
    $[56] = t1;
    $[57] = t2;
    $[58] = t3;
    $[59] = t4;
    $[60] = t5;
    $[61] = t6;
    $[62] = t7;
  } else {
    t7 = $[62];
  }
  return t7;
};
function _temp(s) {
  return s + 1;
}